/**
 * The keep-open region of a pop-up menu (spec §8.6 item 5).
 *
 * A menu does not close at the exact edge of its panel: the pointer has to leave a tolerance region
 * around it, so a hand that slips a few pixels off the edge keeps the menu. The region has two
 * phases — wider before the pointer has ever been inside the panel, and anchored on the button as
 * well so the gap between button and panel is covered; narrower once the pointer has been inside.
 *
 * Pure geometry, so the arithmetic is testable without a DOM (as `dropZones.ts` is).
 */

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Fractions of the panel's width; the floor keeps the tolerance usable on a narrow panel. */
export const MARGIN_BEFORE_ENTERED = 0.2;
export const MARGIN_AFTER_ENTERED = 0.1;
export const MIN_MARGIN_PX = 12;

export function tolerance(panelWidth: number, entered: boolean): number {
  return Math.max(MIN_MARGIN_PX, panelWidth * (entered ? MARGIN_AFTER_ENTERED : MARGIN_BEFORE_ENTERED));
}

export function union(a: Rect, b: Rect | null): Rect {
  if (!b) return a;
  return { left: Math.min(a.left, b.left), top: Math.min(a.top, b.top), right: Math.max(a.right, b.right), bottom: Math.max(a.bottom, b.bottom) };
}

export function grow(r: Rect, by: number): Rect {
  return { left: r.left - by, top: r.top - by, right: r.right + by, bottom: r.bottom + by };
}

export function contains(r: Rect, x: number, y: number): boolean {
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

/**
 * Where the pointer may be without closing the menu: the panel alone once it has been inside,
 * otherwise the panel together with the button that opened it.
 */
export function keepOpenRegion(panel: Rect, button: Rect | null, entered: boolean): Rect {
  const base = entered ? panel : union(panel, button);
  return grow(base, tolerance(panel.right - panel.left, entered));
}
